<script>
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required } from '@vuelidate/validators';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ZaloPersonalChannel from 'dashboard/api/channel/zaloPersonalChannel';

const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 120000;

export default {
  components: { PageHeader, NextButton },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      inboxName: '',
      loading: false,
      polling: false,
      qrDataUrl: null,
      inboxId: null,
      pollHandle: null,
      pollStartedAt: 0,
    };
  },
  validations: { inboxName: { required } },
  beforeUnmount() {
    if (this.pollHandle) clearInterval(this.pollHandle);
  },
  methods: {
    async startSetup() {
      this.v$.$touch();
      if (this.v$.$invalid) return;
      this.loading = true;
      try {
        const { data } = await ZaloPersonalChannel.startQrSession(
          this.inboxName
        );
        this.inboxId = data.inbox_id;
        this.startPolling();
      } catch (error) {
        this.loading = false;
        useAlert(
          error.message ||
            this.$t('INBOX_MGMT.ADD.ZALO_PERSONAL.API.START_ERROR')
        );
      }
    },
    startPolling() {
      this.polling = true;
      this.pollStartedAt = Date.now();
      this.pollHandle = setInterval(() => this.pollQr(), POLL_INTERVAL_MS);
      this.pollQr();
    },
    async pollQr() {
      try {
        const { data } = await ZaloPersonalChannel.qrStatus(this.inboxId);
        this.qrDataUrl = data.qr_data_url;
        if (data.status === 'connected') {
          this.stopPollingAndFinish();
          return;
        }
        if (Date.now() - this.pollStartedAt > POLL_TIMEOUT_MS) {
          await ZaloPersonalChannel.restartQr(this.inboxId);
          this.pollStartedAt = Date.now();
        }
      } catch (err) {
        // keep polling; transient network errors are expected
        // eslint-disable-next-line no-console
        console.warn('[ZaloPersonal] pollQr error:', err?.message ?? err);
      }
    },
    stopPollingAndFinish() {
      if (this.pollHandle) clearInterval(this.pollHandle);
      this.polling = false;
      this.$router.push({
        name: 'settings_inbox_finish',
        params: { page: 'new', inbox_id: this.inboxId },
      });
    },
  },
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader
      :header-title="$t('INBOX_MGMT.ADD.ZALO_PERSONAL.TITLE')"
      :header-content="$t('INBOX_MGMT.ADD.ZALO_PERSONAL.DESC')"
    />
    <form
      v-if="!polling"
      class="flex flex-wrap flex-col mx-0"
      @submit.prevent="startSetup()"
    >
      <label :class="{ error: v$.inboxName.$error }">
        {{ $t('INBOX_MGMT.ADD.ZALO_PERSONAL.INBOX_NAME.LABEL') }}
        <input
          v-model="inboxName"
          type="text"
          :placeholder="
            $t('INBOX_MGMT.ADD.ZALO_PERSONAL.INBOX_NAME.PLACEHOLDER')
          "
          @blur="v$.inboxName.$touch"
        />
      </label>
      <div class="w-full mt-4">
        <NextButton
          :is-loading="loading"
          type="submit"
          solid
          blue
          :label="$t('INBOX_MGMT.ADD.ZALO_PERSONAL.CONNECT_BUTTON')"
        />
      </div>
    </form>

    <div v-else class="flex flex-col items-center mt-6">
      <p class="text-sm mb-4">
        {{ $t('INBOX_MGMT.ADD.ZALO_PERSONAL.QR_INSTRUCTION') }}
      </p>
      <img
        v-if="qrDataUrl"
        :src="qrDataUrl"
        :alt="$t('INBOX_MGMT.ADD.ZALO_PERSONAL.QR_ALT')"
        class="w-64 h-64 border rounded"
      />
      <p v-else class="text-sm text-gray-500">
        {{ $t('INBOX_MGMT.ADD.ZALO_PERSONAL.QR_LOADING') }}
      </p>
    </div>
  </div>
</template>
